(() => {
  'use strict';

  if (window.customElements.get('cart-note-panel')) {
    return;
  }

  /*
   * The cart drawer is re-rendered from the server after every cart change, which recreates this element. The open
   * state and an unsaved draft are kept here so the recreated panel picks up where the shopper left off.
   */
  const restoredState = { open: false, draft: null };

  class CartNotePanel extends HTMLElement {
    connectedCallback() {
      this.toggleButton = this.querySelector('.cart-drawer__note-toggle');
      this.body = this.querySelector('.cart-drawer__note-body');
      this.field = this.querySelector('textarea');
      this.preview = this.querySelector('.cart-drawer__note-preview');
      this.error = this.querySelector('.cart-drawer__note-error');
      this.deleteButton = this.querySelector('.cart-drawer__note-delete');
      this.saveButton = this.querySelector('.cart-drawer__note-save');

      if (!this.toggleButton || !this.body || !this.field) {
        return;
      }

      if (restoredState.draft !== null && restoredState.draft !== this.field.value) {
        this.field.value = restoredState.draft;
      }

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.toggleButton.addEventListener('click', () => this.setOpen(!this.isOpen), { signal });
      this.field.addEventListener('input', () => this.onInput(), { signal });
      this.deleteButton?.addEventListener('click', () => this.clearNote(), { signal });
      this.saveButton?.addEventListener('click', () => this.saveNote(), { signal });

      this.setOpen(restoredState.open);
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    get isOpen() {
      return this.toggleButton.getAttribute('aria-expanded') === 'true';
    }

    setOpen(open) {
      this.toggleButton.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.body.hidden = !open;
      this.classList.toggle('is-open', open);
      restoredState.open = open;
      this.refresh();
    }

    onInput() {
      restoredState.draft = this.field.value;
      this.setError(false);
      this.refresh();
    }

    /* The delete button only works on a typed message; the preview shows it while the field is closed */
    refresh() {
      const value = this.field.value.trim();

      if (this.deleteButton) {
        this.deleteButton.disabled = value === '';
      }

      if (this.preview) {
        this.preview.textContent = value;
        this.preview.hidden = this.isOpen || value === '';
      }
    }

    setError(visible) {
      if (this.error) {
        this.error.hidden = !visible;
      }
    }

    /* A failed delete puts the message back, so the field never shows less than the cart still carries */
    async clearNote() {
      const previousValue = this.field.value;

      this.field.value = '';
      restoredState.draft = '';
      this.refresh();

      const saved = await this.persist(this.deleteButton);

      if (!saved) {
        this.field.value = previousValue;
        restoredState.draft = previousValue;
        this.refresh();
      }
    }

    async saveNote() {
      await this.persist(this.saveButton);
    }

    async persist(button) {
      const note = this.field.value;

      button?.setAttribute('aria-busy', 'true');
      this.setError(false);

      try {
        const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note })
        });

        if (!response.ok) {
          throw new Error(`cart/update.js responded with ${response.status}`);
        }

        restoredState.draft = null;
        return true;
      } catch {
        this.setError(true);
        return false;
      } finally {
        button?.removeAttribute('aria-busy');
      }
    }
  }

  window.customElements.define('cart-note-panel', CartNotePanel);
})();
